import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../Sidebar";
import { useLocation } from "react-router-dom";
import { FaMap } from "react-icons/fa";
import { LayoutDashboard, Info } from "lucide-react";
import axios from "axios";
import { getDB, dbGet, dbPut } from "../../db";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import "../sprzedaz/DashboardGrid.css";
import ZakupyTopCitiesTable from "./ZakupyTopCitiesTable";
import ZakupyByPolishRegionsTable from "./ZakupyByPolishRegionsTable";
import ZakupyByRegionChart from "./ZakupyTransactionsByRegionChart";
import AveragePurchaseValueByRegionChart from "./AveragePurchaseValueByRegionChart";
import UniqueSuppliersByRegionChart from "./UniqueSuppliersByRegionChart";
import ZakupyChangesByRegion from "./ZakupyChangesByRegion";
import ZakupyByPolandRegionChart from "./ZakupyByPolandRegionChart";
import ZakupyByCountry from "./ZakupyByCountry";
import ZakupyByCountryChart from "./ZakupyByCountryChart";
import ZakupyChangesByCountry from "./ZakupyChangesByCountry";
import DomesticVsInternationalPurchases from "./DomesticVsInternationalPurchases";
import TopBar from "../TopBar";
import styled, { createGlobalStyle } from "styled-components";

// NEW: shared, license-gated download buttons + token decode
import { DownloadButtons } from "../DownloadButtons";
import { jwtDecode } from "jwt-decode";

/* ----------------------- EXPORT MODE (generic) ----------------------- */
const ExportModeStyles = createGlobalStyle`
  .for-export * { pointer-events: none !important; cursor: default !important; }
  .for-export [data-export-hide="true"] { display: none !important; }
`;
const enableExportMode = () => document.body.classList.add("for-export");
const disableExportMode = () => document.body.classList.remove("for-export");
/* -------------------------------------------------------------------- */

const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "8px",
  padding: "20px 20px 40px 20px",
  backgroundColor: "#f9f9f9",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
  textAlign: "center",
};

const formatPath = (path) => {
  const customNames = {
    "zakupy/region": {
      name: "Zakupów / Zakupy wg regionów",
      icon: <FaMap />,
    },
  };
  const cleanPath = path.replace(/^\//, "");
  return customNames[cleanPath] || { name: cleanPath.replace("/", " / "), icon: null };
};

const AlertNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #facc15;
  background: #fef9c3;
  color: #7c6f05;
  border-radius: 8px;
  text-align: left;
  font-size: 0.9rem;
  line-height: 1.35;
  margin-bottom: 20px;
`;

const SectionTitle = ({ title, inGrid = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 20px 12px 20px",
      margin: inGrid ? "8px 0 12px 0" : "8px 20px 12px 20px",
      fontSize: "1.05rem",
      fontWeight: 700,
      color: "#2b2b2b",
      position: "relative",
      background: "linear-gradient(to right, rgba(235, 135, 227, 0.12), rgba(227, 135, 235, 0.04))",
      border: "1px solid rgba(235, 135, 230, 0.35)",
      borderRadius: "8px",
      ...(inGrid ? { gridColumn: "1 / -1", paddingLeft: "20px", paddingRight: "20px" } : {}),
    }}
  >
    <LayoutDashboard size={20} strokeWidth={2} color="#eb87deff" />
    <span style={{ position: "relative", display: "inline-block" }}>
      {title}
      <span
        style={{
          position: "absolute",
          left: 0,
          bottom: -3,
          width: "100%",
          height: 2,
          backgroundColor: "#eb87beff",
          borderRadius: 2,
          opacity: 0.7,
        }}
      />
    </span>
  </div>
);

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const hideCardControlsTemporarily = (rootEl) => {
  const controls = Array.from(
    rootEl.querySelectorAll(
      'input, select, button, [data-export-hide="true"], .filters, .control-row, .toolbar'
    )
  );
  controls.forEach((el) => {
    el.__prevDisplay = el.style.display;
    el.style.display = "none";
  });
  return () => {
    controls.forEach((el) => {
      el.style.display = el.__prevDisplay || "";
      delete el.__prevDisplay;
    });
  };
};
const captureElementAsJpeg = async (el) => {
  el.scrollIntoView({ block: "center" });
  const restore = hideCardControlsTemporarily(el);
  await wait(200);
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    ignoreElements: (node) => {
      if (!(node instanceof Element)) return false;
      return node.matches('[data-export-hide="true"]');
    },
  });
  restore();
  const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
  return { dataUrl, width: canvas.width, height: canvas.height };
};

// Helper: extract license info from token
function extractLicenseFromToken(token) {
  if (!token) return null;
  try {
    const d = jwtDecode(token) || {};
    const typeRaw = d.LicenseType || d.licenseType || d.plan || d.Plan || "";
    const expRaw  = d.LicenseExpire ?? d.licenseExpire ?? "";

    const t = String(typeRaw).toUpperCase();
    const e = String(expRaw).toUpperCase();

    const typeHasNoExp = t.includes("NO EXPIRE") || t.includes("NO_EXPIRE");
    const expIsFlag    = e === "NO EXPIRE" || e === "NO_EXPIRE";

    let yearIsFar = false;
    if (!expIsFlag && typeof expRaw === "string" && expRaw) {
      const dt = new Date(expRaw);
      yearIsFar = !isNaN(dt) && dt.getUTCFullYear() >= 2099;
    }

    if (typeHasNoExp || expIsFlag || yearIsFar) return "NO EXPIRE";
    return typeRaw || null;
  } catch {
    return null;
  }
}

const ZakupyRegion = () => {
  const [email, setEmail] = useState("");
  const [purchaseData, setPurchaseData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const locationData = useLocation();
  const { name, icon } = formatPath(locationData.pathname);

  // NEW: license
  const [licenseType, setLicenseType] = useState(null);

  const refs = {
    topCitiesRef: useRef(null),
    revenueRegionsTableRef: useRef(null),
    transactionsRegionRef: useRef(null),
    avgTransactionRegionRef: useRef(null),
    uniqueContractorsRegionRef: useRef(null),
    salesChangesRegionRef: useRef(null),
    revenuePolandRegionRef: useRef(null),
    domesticVsIntlRef: useRef(null),
    revenueCountryRef: useRef(null),
    revenueCountryChartRef: useRef(null),
    salesChangesCountryRef: useRef(null),
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user?.Email) setEmail(user.Email);

    // license from user object
    const ltUser =
      user?.licenseType ?? user?.LicenseType ?? user?.plan ?? user?.Plan ?? null;
    if (typeof ltUser === "string" && ltUser) setLicenseType(ltUser);

    // license from token
    const token = localStorage.getItem("token");
    const ltFromToken = extractLicenseFromToken(token);
    if (ltFromToken) setLicenseType(ltFromToken);
  }, []);

  useEffect(() => {
    const fetchPurchaseData = async () => {
      try {
        await getDB();
        const cached = await dbGet("zakupy", "allZakupy");
        setPurchaseData(cached?.data || []);
      } catch (e) {
        console.error("fetchPurchaseData error:", e);
        setPurchaseData([]);
      }
    };
    const fetchClientsData = async () => {
      try {
        await getDB();
        const now = Date.now();
        const cached = await dbGet("clients", "allClients");
        if (cached && now - cached.timestamp < 60 * 60 * 1000) {
          setClientsData(cached.data);
          return;
        }
        const res = await axios.get("https://server229256.nazwa.pl/api/klienci");
        await dbPut("clients", "allClients", { data: res.data, timestamp: now });
        setClientsData(res.data);
      } catch (err) {
        console.error("fetchClientsData error:", err);
        setClientsData([]);
      }
    };
    fetchPurchaseData();
    fetchClientsData();
  }, []);

  // === REPORT LAYOUTS ===
  const COMPONENTS_FOR_REPORT = [
    { ref: refs.topCitiesRef,            title: "Top Miasta — Zakupy (Tabela)",     layout: "landscape-2col" },
    { ref: refs.revenueRegionsTableRef,  title: "Województwa — Zakupy (Tabela)",    layout: "landscape-2col" },

    { ref: refs.transactionsRegionRef,       title: "Transakcje wg województw",         layout: "landscape-3col" },
    { ref: refs.avgTransactionRegionRef,     title: "Średnia wartość wg województw",    layout: "landscape-3col" },
    { ref: refs.uniqueContractorsRegionRef,  title: "Unikalni dostawcy wg województw",  layout: "landscape-3col" },

    { ref: refs.salesChangesRegionRef,       title: "Zmiany zakupów wg województw",     layout: "landscape" },

    { ref: refs.domesticVsIntlRef,           title: "Zakupy: krajowe vs zagraniczne",   layout: "landscape-2col" },

    { ref: refs.revenueCountryRef,           title: "Zakupy wg krajów (Tabela)",        layout: "landscape-2col" },

    { ref: refs.salesChangesCountryRef,      title: "Zmiany zakupów wg krajów",         layout: "landscape" },
  ];

  const buildPages = async () => {
    const P_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
    const L_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
    const pages = [];

    const getPageMetrics = (orient) => {
      const isLandscape = orient === "l" || orient === "l2" || orient === "l3";
      const base = isLandscape
        ? { pageW: 297, pageH: 210, margins: L_MARGINS }
        : { pageW: 210, pageH: 297, margins: P_MARGINS };
      const contentW = base.pageW - base.margins.left - base.margins.right;
      const contentH = base.pageH - base.margins.top - base.margins.bottom;
      return { ...base, contentW, contentH };
    };

    let curY = null;
    let l3colCountOnCurrentPage = 0;
    let l2colCountOnCurrentPage = 0;

    const addBlock = (img, title, layout) => {
      const gutter = 6;

      if (layout === "landscape-3col") {
        if (!pages.length || pages[pages.length - 1].orient !== "l3" || l3colCountOnCurrentPage >= 3) {
          pages.push({ orient: "l3", elements: [] });
          l3colCountOnCurrentPage = 0;
        }
        const { margins, contentW, contentH } = getPageMetrics("l");
        const columns = 3;
        const cellW = (contentW - gutter * (columns - 1)) / columns;
        const cellH = contentH * 0.9;

        const col = l3colCountOnCurrentPage;
        const x = margins.left + col * (cellW + gutter);
        const y = margins.top;

        const ratio = img.height / img.width;
        let w = cellW;
        let h = w * ratio;
        if (h > cellH) { h = cellH; w = h / ratio; }

        pages[pages.length - 1].elements.push({
          x, y: y + (cellH - h) / 2, w, h, title, dataUrl: img.dataUrl,
        });
        l3colCountOnCurrentPage += 1;
        return;
      }

      if (layout === "landscape-2col") {
        if (!pages.length || pages[pages.length - 1].orient !== "l2" || l2colCountOnCurrentPage >= 2) {
          pages.push({ orient: "l2", elements: [] });
          l2colCountOnCurrentPage = 0;
        }
        const { margins, contentW, contentH } = getPageMetrics("l");
        const columns = 2;
        const cellW = (contentW - gutter * (columns - 1)) / columns;
        const cellH = contentH * 0.9;

        const col = l2colCountOnCurrentPage;
        const x = margins.left + col * (cellW + gutter);
        const y = margins.top;

        const ratio = img.height / img.width;
        let w = cellW;
        let h = w * ratio;
        if (h > cellH) { h = cellH; w = h / ratio; }

        pages[pages.length - 1].elements.push({
          x, y: y + (cellH - h) / 2, w, h, title, dataUrl: img.dataUrl,
        });
        l2colCountOnCurrentPage += 1;
        return;
      }

      if (layout === "landscape") {
        pages.push({ orient: "l", elements: [] });
        const { margins, contentW, contentH } = getPageMetrics("l");
        const ratio = img.height / img.width;
        const targetW = contentW;
        const targetH = Math.min(targetW * ratio, contentH * 0.9);
        const startY = margins.top + (contentH - targetH) / 2;

        pages[pages.length - 1].elements.push({
          x: margins.left, y: startY, w: targetW, h: targetH, title, dataUrl: img.dataUrl,
        });
        return;
      }

      // portrait flow
      const { margins, contentW, contentH } = getPageMetrics("p");
      const targetW = contentW;
      const ratio = img.height / img.width;
      const targetH = Math.min(targetW * ratio, contentH * 0.9);

      if (!pages.length || pages[pages.length - 1].orient !== "p") {
        pages.push({ orient: "p", elements: [] });
        const { margins: m } = getPageMetrics("p");
        curY = m.top;
      }
      if (curY + targetH > margins.top + contentH) {
        pages.push({ orient: "p", elements: [] });
        curY = margins.top;
      }

      pages[pages.length - 1].elements.push({
        x: margins.left, y: curY, w: targetW, h: targetH, title, dataUrl: img.dataUrl,
      });
      curY += targetH + 6;
    };

    for (const block of COMPONENTS_FOR_REPORT) {
      if (!block.ref?.current) continue;
      const img = await captureElementAsJpeg(block.ref.current);
      addBlock(img, block.title, block.layout);
    }

    return { pages, getPageMetrics };
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const reportTitle = "Zakupy wg regionów — Raport";
      const { pages, getPageMetrics } = await buildPages();

      if (pages.length === 0) pages.push({ orient: "p", elements: [] });
      const firstOrient = pages[0].orient === "p" ? "portrait" : "landscape";
      const pdf = new jsPDF({ orientation: firstOrient, unit: "mm", format: "a4", compress: true });

      const drawHeaderFooter = (pdf, page, pageCount, opts) => {
        const { leftMargin, topMargin, rightMargin, bottomMargin, title } = opts;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(title, leftMargin, topMargin - 5, { baseline: "bottom" });
        pdf.setLineWidth(0.2);
        pdf.line(leftMargin, topMargin - 3, pdf.internal.pageSize.getWidth() - rightMargin, topMargin - 3);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        const pageLabel = `Page ${page} / ${pageCount}`;
        const dateLabel = new Date().toISOString().slice(0, 19).replace("T", " ");
        const y = pdf.internal.pageSize.getHeight() - bottomMargin + 5;
        pdf.text(pageLabel, leftMargin, y);
        const w = pdf.getTextWidth(dateLabel);
        pdf.text(dateLabel, pdf.internal.pageSize.getWidth() - rightMargin - w, y);
      };

      const pageCount = pages.length;
      pages.forEach((pg, i) => {
        if (i > 0) {
          const ori = pg.orient === "p" ? "portrait" : "landscape";
          pdf.addPage("a4", ori);
        }
        const { margins } = getPageMetrics(pg.orient);
        drawHeaderFooter(pdf, i + 1, pageCount, {
          leftMargin: margins.left,
          rightMargin: margins.right,
          topMargin: margins.top,
          bottomMargin: margins.bottom,
          title: reportTitle,
        });
        pg.elements.forEach((el) => {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(10);
          const titleY = el.y - 2;
          if (titleY > margins.top) pdf.text(el.title, el.x, titleY);
          pdf.addImage(el.dataUrl, "JPEG", el.x, el.y, el.w, el.h, undefined, "FAST");
        });
      });

      pdf.save(`Zakupy_regiony_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas generowania PDF.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  const handleDownloadPNG = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const zip = new JSZip();

      for (const c of COMPONENTS_FOR_REPORT) {
        if (!c.ref?.current) continue;
        c.ref.current.scrollIntoView({ block: "center" });
        const restore = hideCardControlsTemporarily(c.ref.current);
        await wait(200);
        const canvas = await html2canvas(c.ref.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          ignoreElements: (node) => {
            if (!(node instanceof Element)) return false;
            return node.matches('[data-export-hide="true"]');
          },
        });
        restore();

        const imgData = canvas.toDataURL("image/png");
        zip.file(`${c.title.replace(/\s+/g, "_")}.png`, imgData.split("base64,")[1], { base64: true });
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Zakupy_regiony_${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas generowania PNG.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  return (
    <>
      <ExportModeStyles />

      <div style={{ display: "flex", height: "100vh", flexDirection: "row" }}>
        <Sidebar />

        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <TopBar />

          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 20px",
              fontSize: "1.2rem",
              fontWeight: "bold",
              color: "#9848c7",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {icon} {name}
            </div>

            {/* License-gated export buttons */}
            <DownloadButtons
              licenseType={licenseType}
              onDownloadPDF={handleDownloadPDF}
              onDownloadPNG={handleDownloadPNG}
              disabled={isDownloading}
            />
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: "auto", padding: "20px", backgroundColor: "#f5f5f5" }}>
            <SectionTitle inGrid title="Analiza zakupów wg miast i województw" />

            <AlertNote>
              <Info size={18} style={{ minWidth: 18, marginTop: 2 }} />
              <span>
                Dane z Polski zostaną wyświetlone, gdy nasze API poprawnie pobierze dane Twoich
                klientów/dostawców. Prosimy o cierpliwość i wróć za kilka godzin (proces może potrwać do 24 godzin).
              </span>
            </AlertNote>

            {/* SECTION 1 */}
            <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
              <div className="card-span-6" style={cardStyle} ref={refs.topCitiesRef}>
                <ZakupyTopCitiesTable purchaseData={purchaseData} clientsData={clientsData} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={refs.revenueRegionsTableRef}>
                <ZakupyByPolishRegionsTable purchaseData={purchaseData} clientsData={clientsData} />
              </div>
            </div>

            {/* SECTION 2 */}
            <SectionTitle inGrid title="Analiza liczby transakcji i wielkości zakupów wg regionów" />
            <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
              <div className="card-span-4" style={cardStyle} ref={refs.transactionsRegionRef}>
                <ZakupyByRegionChart purchaseData={purchaseData} clientsData={clientsData} />
              </div>
              <div className="card-span-4" style={cardStyle} ref={refs.avgTransactionRegionRef}>
                <AveragePurchaseValueByRegionChart purchaseData={purchaseData} clientsData={clientsData} />
              </div>
              <div className="card-span-4" style={cardStyle} ref={refs.uniqueContractorsRegionRef}>
                <UniqueSuppliersByRegionChart purchaseData={purchaseData} clientsData={clientsData} />
              </div>
              <div className="card-span-12" style={cardStyle} ref={refs.salesChangesRegionRef}>
                <ZakupyChangesByRegion purchaseData={purchaseData} clientsData={clientsData} />
              </div>
            </div>

            {/* SECTION 3 */}
            <SectionTitle inGrid title="Przegląd map zakupów, udział zakupów krajowych i zagranicznych" />
            <div className="dashboard-grid">
              <div className="card-span-6" style={cardStyle} ref={refs.revenuePolandRegionRef}>
                <ZakupyByPolandRegionChart purchaseData={purchaseData} clientsData={clientsData} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={refs.domesticVsIntlRef}>
                <DomesticVsInternationalPurchases purchaseData={purchaseData} clientsData={clientsData} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={refs.revenueCountryRef}>
                <ZakupyByCountry purchaseData={purchaseData} clientsData={clientsData} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={refs.revenueCountryChartRef}>
                <ZakupyByCountryChart purchaseData={purchaseData} clientsData={clientsData} />
              </div>
              <div className="card-span-12" style={cardStyle} ref={refs.salesChangesCountryRef}>
                <ZakupyChangesByCountry purchaseData={purchaseData} clientsData={clientsData} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDownloading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: "#333",
            zIndex: 9999,
          }}
        >
          Poczekaj, trwa generowanie raportu... ⏳
        </div>
      )}
    </>
  );
};

export default ZakupyRegion;
